export enum BILLING_FLAGS {
  EVENT_TIMESTAMP_BILLING = `Event Timestamp Billing`,
  BUSINESS_ASSOCIATE_AGREEMENT = `Business Associate Agreement`,
}

export interface CreditInfo {
  applies_to?: string;
  credit_type_id?: number;
  end_date?: string;
  initial_value?: string;
  name?: string;
  remaining_balance?: string;
  state?: string;
}

export interface CardInfo {
  type: string;
  last4: number;
  exp_month: number;
  exp_year: number;
  address_country: string;
  address_zip: string;
}

export interface Usage {
  last_month_mtu_usage: number;
  last_month_events_usage: number;
  usage_is_partial_month: boolean;
  term_events_tally: number;
  term_mtu_tally: number;
  spark: number;
}

export enum AgreementState {
  PROPOSED = `PROPOSED`,
  AGREED = `AGREED`,
  CANCELED = `CANCELED`,
  BILLING_ACTIVE = `BILLING_ACTIVE`,
  BILLING_TERMINATED = `BILLING_TERMINATED`,
  BILLING_COMPLETED = `BILLING_COMPLETED`,
}

export enum CommonBuyables {
  EVENTS = `Events`,
  MTUS = `Monthly Tracked Users`,
  SESSION_REPLAY = `Session Replays`,
  WAREHOUSE_CONNECTORS = `Warehouse Connectors`,
}

export enum BuyableId {
  EVENTS = 1,
  FREE_PLAN = 8,
  SPARK_AI = 9,
  GROWTH_PLAN = 12,
  PIPELINES = 16,
  GROUP_ANALYTICS_6 = 19,
  MONTHLY_TO_ANNUAL_DISCOUNT = 21,
  ENTERPRISE_PLAN = 25,
  WAREHOUSE_CONNECTORS = 27,
  MTUS = 31,
  SESSION_REPLAY = 33,
  ENTERPRISE_PLAN_WITH_PROFILES = 36,
  GROUP_ANALYTICS_3 = 39,
  BUSINESS_ASSOCIATE_AGREEMENT = 41,
  SIGNATURE_SUCCESS_PACKAGE = 44,
  PREMIER_SUCCESS_PACKAGE = 48,
  ESSENTIAL_SUCCESS_PACKAGE = 51,
  GCP_PLAN_CREDIT = 55,
  RESELLER_CREDIT = 59,
  ONE_TIME_CREDIT = 60,
  SILVER_SUPPORT = 71,
  GOLD_SUPPORT = 73,
  STANDARD_ONBOARDING_PACKAGE = 77,
  ENTERPRISE_TRIAL_PLAN = 80,
  INDIA_DATACENTER_ACCESS = 84,
  STRATEGIC_ONBOARDING_PACKAGE = 88,
  PEOPLE = 92,
  PROFESSIONAL_SUCCESS_PACKAGE = 93,
  PARTNERSHIP_ENTERPRISE_PLAN = 99,
  INDIA_DATACENTER_MIGRATION_SERVICES = 101,
  PLATINUM_SUPPORT = 104,
  PREMIUM_SUPPORT_PLAN = 111,
  ENTERPRISE_STARTUP_PLAN_BUYABLE_ID = 112,
  MCDONALDS_CUSTOM_SUPPORT = 116,
  SESSION_REPLAY_RETENTION_PERIOD = 127,
  CUSTOM_EVENTS_RETENTION_PERIOD = 135,
  HISTORICAL_EVENTS_CREDIT = 139,
  EXPERIMENTS_MONTHLY_USERS = 144,
  METRIC_TREE = 145,
  FEATURE_FLAGS_REQUESTS = 146,
  ENABLED_FEATURE_FLAGS = 147,
  GROUP_ANALYTICS_1 = 160,
}

export enum BuyableFeatureGate {
  MCDONALDS_CUSTOM_SERVICE_PLAN = `mcdonalds_custom_service_plan`,
  PLATINUM_SERVICE_PLAN = `platinum_service_plan`,
  GOLD_SERVICE_PLAN = `gold_service_plan`,
  SILVER_SERVICE_PLAN = `silver_service_plan`,
  STANDARD_ONBOARDING_PACKAGE = `standard_onboarding_package`,
  STRATEGIC_ONBOARDING_PACKAGE = `strategic_onboarding_package`,
  ENTERPRISE = `enterprise`,
  ENTERPRISE_PLAN_WITH_PROFILES = `enterprise_datapoints_with_profiles`,
  MTU_ENTERPRISE = `mtu_enterprise`,
}

export enum BuyableType {
  METERED = `METERED`,
  ADDON = `ADDON`,
  DISCOUNT = `DISCOUNT`,
  BASE_FEATURE_PLAN = `BASE_FEATURE_PLAN`,
}

export interface BuyableMetadata {
  ui_enable_manual_provision?: boolean;
  ui_enable_overage_calc?: boolean;
  ui_enable_unlimited?: boolean; // Whether this metered buyable may be provisioned as an unlimited (infinite quantity) line
  usage_based?: boolean; // Indicates if the buyable is usage-based, doesn't affect total price
  tax_exempt?: boolean; // When true, invoice lines skip Anrok; tax is specified directly via sales_tax_amount
}

export interface Buyable {
  id: number;
  name: string;
  buyable_type: BuyableType;
  metadata: BuyableMetadata;
  feature_gate_name?: string | null;
  metered_unit_name?: string | null;
}

// Type that holds a spec to allow the backend to construct a custom billing cycle
export type BillingCycleSpec = {
  duration: number;
  offsets_and_weights: Record<number, number>;
};

export type AgreementLineMetadata = {
  retention_days?: number;
  bucket_months?: number; // Optional parameter; subdivides the term into buckets of this length
  should_autorenew?: boolean; // Optional parameter; determines if the line should autorenew
  mtu_threshold?: number; // Optional parameter; minimum threshold for an MTU if not 1
  custom_event_retention_days?: number; // custom retention days for custom events buyable
  prorate_date?: string; // Optional date for prorating the line price
  prorate_type?: string; // Optional type for prorating the line price
  included_by_default?: boolean; // Optional parameter; indicates if the line is included by default
  is_default_amount?: boolean; // Optional parameter; indicates if the line is the default amount for the agreement
  list_quantity?: number; // Optional parameter; indicates the list quantity for the line (before proration)
  credited_unit_amount?: number; // Number of units (e.g., events) credited for historical event forgiveness
  should_grandfather?: boolean; // Optional parameter; indicates if the SKU is grandfathered
  is_unlimited?: boolean; // Optional parameter; metered line with no usage cap (infinite quantity). Quantity is 0 when set.
  sales_tax_amount?: string; // Pre-specified tax amount for tax-exempt buyables
};

export interface AgreementLine {
  id: number;
  buyable_id: number;
  buyable_name: string;
  buyable_type: BuyableType;
  buyable_metadata?: BuyableMetadata;
  feature_gate_name?: string;
  quantity: number;
  // TODO: python serializes decimal to string
  // consider adjusting the API to return a number instead
  net_price: string;
  list_price: string;
  start_date: string;
  end_date: string;
  term_length_in_months: any;
  billing_cycle_id: number;
  memo: string;
  custom_overage_rate?: string;
  metadata?: AgreementLineMetadata;
  // Below: optional fields only used during agreement provisioning
  billing_cycle_spec?: BillingCycleSpec; // specs are used to construct custom billing cycles
  overage_rate_multiplier?: string; // multiplier is used to calculate overage rate while provisioning metered buyable
}

export interface AgreementLineTemplate {
  expected_duration_months: number;
}

// Common interface for both Template and Agreement
export interface Agreement {
  id: number;
  name: string;
  account_id?: number;
  agreement_template_id?: number;
  start_date: string;
  planned_end_date: string;
  sold_by?: string;
  state?: AgreementState;
  sent_to_collections_date?: string;
  completed_date?: string;
  signed_date_time?: string;
  agreement_lines: AgreementLine[];
  should_autorenew: boolean;
  allow_self_serve_changes: boolean;
  is_self_serve: boolean;
  renewal_agreement_id?: number;
  renewal_start_date?: string;
  renewal_planned_end_date?: string;
  related_agreements: Agreement[];
  parent_agreement_id?: number;
  scheduled_cancel_date?: string;
  should_amortize_arr?: boolean;
  is_trial_agreement?: boolean;
}

export type AgreementTemplate = Agreement & {
  agreement_lines: AgreementLineTemplate[];
  expected_duration_months: number;
  group_name?: string;
  is_active_curve: boolean;
};

export type FreePlanPeriod = {
  start_date: string;
  end_date: string | null; // null means ongoing
};

export type BumpAgreementHistory = {
  agreements: Agreement[];
  free_plan_periods: FreePlanPeriod[];
};

type PredictedUsage = {
  predicted_usage: number;
  predicted_overage_cost: number;
};

type BucketedUsage = {
  bucket_start: string;
  bucket_end: string;
  bucket_cap: number;
  bucket_months: number;
  usage_this_bucket: number;
};

type TermUsageBase = {
  buyable_id: number;
  buyable_name: string;
  usage_this_term: number;
  predicted_usage_this_term: PredictedUsage;
  term_start: string;
  term_end?: string;
  bucketed_usage?: BucketedUsage;
};

// Discriminated on is_unlimited: the unlimited variant has NO cap/term_cap, so
// the compiler rejects any cap/term_cap read until the code narrows on
// is_unlimited (e.g. `if (!usage.is_unlimited) { ...usage.cap... }`). This is a
// "property does not exist" error (TS2339), not a strict-mode error, so it is
// enforced even in strict-ignored files and through bracket access. Unlimited
// buyables have no finite cap; the backend sends null (the 10**18 sentinel stays
// internal). cap/term_cap are nullable because the backend also sends null for
// no-allocation fallbacks.
export type UnlimitedTermUsage = TermUsageBase & {is_unlimited: true};
export type CappedTermUsage = TermUsageBase & {
  is_unlimited?: false;
  cap?: number | null;
  term_cap?: number | null;
};
export type TermUsage = UnlimitedTermUsage | CappedTermUsage;

// Type guard for narrowing a TermUsage before reading cap/term_cap. Prefer this
// over a bare `usage.is_unlimited` check: a user-defined type guard narrows the
// union reliably even in strict-ignored files, where truthiness narrowing of
// the discriminant is not applied.
export function isUnlimitedUsage(usage: TermUsage): usage is UnlimitedTermUsage {
  return usage.is_unlimited === true;
}

export type RetentionPeriodInfo = {
  events_retention_days: number;
  events_retention_is_custom: boolean;
  events_retention_has_pending_change: boolean;
};

export interface BumpBillingData {
  account_id: number;
  organization_id: number;
  organization_name: string;
  account_manager: string;
  balance: number;
  current_user_has_staff_billing_permission: boolean;
  current_user_has_monetization_permission: boolean;
  available_credit: number;
  invoice_grace_period: number;
  warning_status: string;
  notes: string;
  payment_type: string;
  is_churned: boolean;
  date_churned: string;
  bcc_email: string;
  receipt_message: string;
  downgrade_reasons: string[];
  credits: CreditInfo[];
  card_info?: CardInfo;
  overage_rate_events: string;
  overage_rate_mtus: string;
  overage_rate_session_replays: string;
  overage_rate_experiments: string;
  overage_rate_feature_flags: string;
  addon_feature_gates: string[];
  feature_gate_name: string;
  is_free: boolean;
  is_paid: boolean;
  is_selfserve: boolean;
  is_enterprise: boolean;
  is_on_contract: boolean;
  is_startup: boolean;
  is_on_latest_curve: boolean;
  is_bump_pb_enabled: boolean;
  self_serve_curve_id: number;
  self_serve_curve_name: string;
  exempt_from_events_warnings: boolean;
  exempt_from_invoice_receipts: boolean;
  suppress_all_mail: boolean;
  hide_billing_reseller: boolean;
  allowed_changes: {
    can_churn_online: boolean;
    can_downgrade_online: boolean;
    can_purchase_online: boolean;
    can_upgrade_online: boolean;
  };
  usage: Usage;
  current_agreements: Agreement[];
  upcoming_agreements: Agreement[];
  metered_buyable_usage_this_term: TermUsage[];
  do_not_bill?: boolean;
  currently_enabled_billing_flags: BILLING_FLAGS[];
  retention_period_info?: RetentionPeriodInfo;
  change_requests?: AccountChangeRequest[];
}

export interface AccountChangeRequest {
  id: string;
  account_id: number;
  organization_name: string;
  requester_email: string;
  request_type: string;
  status: string;
  reason: string;
  metadata: Record<string, unknown>;
  reviewed_by_email: string | null;
  reviewed_at: string | null;
  notes: string;
  created: string;
  modified: string;
  // Whether the current user is authorized to review (approve/deny) this
  // specific request. For data-deletion holds this reflects gtm/legal group
  // membership, not just billing-admin status.
  can_review: boolean;
}

// Top level keys are buyable types (e.g. Events, Spark Queries)
// Second level keys are project IDs (if buyable has per-project usage tracking) or "all"
// Third level (innermost) keys are strings containing the first day of each usage period
// Values are integer usage amounts
export type BumpUsageData = Record<string, Record<string, Record<string, number>>>;

// Historical event import counts: project ID -> date -> count
export type HistoricalEventCounts = Record<string, Record<string, number>>;

// One TallyAdjustment row, itemizing a usage adjustment that is ALREADY
// included in the usage totals (rolled in by the backend before serving).
export type TallyAdjustmentEntry = {
  id: number;
  project_id: number;
  buyable_name: string;
  month: string; // YYYY-MM-DD; first-of-month for Events/MTU, any day for Session Replays
  amount: number; // signed usage delta, already included in usage totals
  memo: string;
  adjustment_source: string;
  created_at: string;
};

// Wrapped response from get_historical_usage endpoint
export type BumpUsageResponse = {
  usage: BumpUsageData;
  historical_event_counts: HistoricalEventCounts;
  tally_adjustments?: TallyAdjustmentEntry[];
};

export type BumpAdminAction = {
  id: number;
  action: string;
  created: string;
  admin: string;
  memo: string;
  description: string;
};

export type BumpAdminActions = BumpAdminAction[];

type BumpEmailNotificationRecipientDict = {
  [key: string]: string;
};

export type BumpEmailNotification = {
  id: number;
  created: string;
  recipients: BumpEmailNotificationRecipientDict;
  type: string;
};

export type BumpEmailNotifications = BumpEmailNotification[];

export type BumpOrganizationMember = {
  user_id: number;
  email: string;
  name: string;
  role: string;
  last_active: string | null;
};

export type BumpOrganizationMembers = BumpOrganizationMember[];

export type BumpProject = {
  project_id: number;
  project_name: string;
  billable_start: string;
  billable_end: string;
  creator: string;
  is_active: boolean;
  cluster: string;
  project_token: string;
  api_key: string | null;
  api_secret: string | null;
};

export type BumpProjects = BumpProject[];

export type BumpUsageDateRange = `12weeks` | `30days` | `6months`;

export type BumpMemberActivityEntry = {
  queries_executed: number;
};

export type BumpMemberActivity = Record<string, BumpMemberActivityEntry>;

export type BumpProjectActivityEntry = {
  queries_executed: number;
  avg_wau: number;
};

export type BumpProjectActivity = Record<string, BumpProjectActivityEntry>;

export type BumpUsageChartSeries = Record<string, Record<string, unknown>>;

export type BumpUsageChartResponse = {
  series: BumpUsageChartSeries;
  date_range?: string[];
};

// One side of the PaymentInvoiceAllocation link, returned inline on either
// payments (linked invoices) or invoices (linked payments).
//
// `amount` is the full invoice (or payment) total — kept for back-compat.
// `amount_applied` is the actual portion of the payment that funded this
// specific invoice; null when the endpoint didn't compute it (e.g. legacy
// callers that didn't pass `allocation_amounts` to to_json). Prefer
// `amount_applied ?? amount` in aggregation logic.
export type BumpAllocatedInvoice = {
  id: number;
  amount: number;
  amount_applied: number | null;
  date: string;
  state: string;
};

export type BumpAllocatedPayment = {
  id: number;
  amount: number;
  amount_applied: number | null;
  date: string;
  state: string;
};

type BumpPaymentInfo = {
  account: number;
  amount: string;
  sales_tax_amount?: string;
  state: string;
  stripe_charge_id?: string;
  stripe_intent_id?: string;
  stripe_intent_status?: string;
  stripe_transfer_id?: string;
  stripe_transfer_date?: string;
  payment_type: string;
  intent_status?: string;
  memo: string;
  created: string;
  last_modified: string;
  invoice_allocations?: BumpAllocatedInvoice[];
};

type BumpPayment = {
  pk: number;
  fields: BumpPaymentInfo;
};

export type BumpPayments = BumpPayment[];

type BumpInvoiceMetadata = {
  adjustment_id?: number;
  adjusted_invoice_line_item_id?: number;
};

export type BumpInvoiceLine = {
  description: string;
  amount: string;
  pre_discount_amount: string;
  sales_tax_amount?: string;
  tax_code: string;
  id: number;
  agreement_id?: number;
  adjustment_memo?: string;
  metadata: BumpInvoiceMetadata;
};

export type BumpInvoice = {
  // TODO: remove unnecessary fields
  id: number;
  account: number;
  due: string;
  block_date: string;
  type: string;
  state: string;
  amount: string;
  sales_tax_amount?: string;
  date: string;
  memo: string;
  is_paid: boolean;
  is_unpaid: boolean;
  is_cancelled: boolean;
  is_overdue: boolean;
  line_items: BumpInvoiceLine[];
  payment_allocations?: BumpAllocatedPayment[];
  legacy: boolean;
  metadata?: Record<string, unknown> | null;
  pdf: string;
};

export type BumpInvoices = BumpInvoice[];

type LegacyPackage = {
  name: string;
  price: string;
};

type LegacyPlanChoice = {
  effective: string;
  plan_name: string;
  price: string;
  cap: string;
  packages?: LegacyPackage[];
};

export type LegacyPlanChoices = {
  events: LegacyPlanChoice[];
  people: LegacyPlanChoice[];
};

type BumpBillingCycleLines = {
  [key: number]: number;
};

export type BumpBillingCycle = {
  id: number;
  name: string;
  duration: number;
  duration_units: string;
  is_stock: boolean;
  billing_cycle_lines: BumpBillingCycleLines;
};

export type BumpBillingCycles = BumpBillingCycle[];

export type Invoice = {
  account: number;
  amount: number;
  block_date: string;
  date: string;
  due: string;
  id: number;
  is_cancelled: boolean;
  is_overdue: boolean;
  is_paid: boolean;
  is_unpaid: boolean;
  legacy: boolean;
  line_items: InvoiceLine[];
  memo?: string;
  sales_tax_amount?: number;
  state: string;
  type: string;
};

type InvoiceLineMetadata = {
  usage_month?: string;
  overage_rate?: string;
  adjusted_invoice_line_item_id?: number;
};

export type InvoiceLine = {
  adjustment_memo?: string;
  agreement_id?: number;
  agreement_line_id?: number;
  amount: number;
  description?: string;
  id: number;
  metadata?: InvoiceLineMetadata;
  pre_discount_amount?: number;
  sales_tax_amount?: number;
  tax_code?: string;
  quantity?: number;
};

export type BillingUserPerms = {
  is_billing_admin: boolean;
  is_monetization: boolean;
  // Populated by the /user_perms endpoint. Optional because some contexts
  // (e.g. the account layout) build this object without it.
  is_staff?: boolean;
};

export type AgreementGroup = {
  id: number;
  name: string;
  metadata: Record<string, any>;
  description: string;
  group_type: string;
};

export type SearchResult = {
  account_id: number;
  organization_name: string;
  organization_id?: number;
  total_contract_value?: number;
};

export type AccountScheduledJobFailure = {
  attempt: number;
  error?: string | null;
  start_time?: string | null;
  end_time?: string | null;
  retried_at: string;
  retried_by: string;
  retry_reason: string;
};

export type AccountScheduledJob = {
  id: string;
  job_name: string;
  job_state: string;
  scheduled_time: string;
  start_time?: string;
  end_time?: string;
  created: string;
  modified: string;
  notifications_sent: string[];
  completion_sent: boolean;
  no_op: boolean;
  help_text?: string;
  // Failure of the current attempt; earlier attempts live in failure_history.
  last_error?: string | null;
  retry_count?: number;
  failure_history?: AccountScheduledJobFailure[];
};
